import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Input, TextareaAutosize, Select, FormTabs, FormTab, formatSelectOptions, PersonPicker } from '../../../components'
import { userService } from '../../../services'
import { useMessage, useModalDialog } from '../../../hooks'
import { useClientCaseDialog } from '../hooks'
import { clientReviewService, clientService } from '../services'
import { appName } from '../appConfig'
import { PERSPECTIVES, emptyPerspective } from '../helpers/perspectives'
import { slugify } from '../helpers'
import '../index.css'

const TABS = ['Översikt', 'Perspektiv', 'Rekrytering', 'Varför denna kund']

const SCORE_LABELS = {
  1: '1 — Minimal',
  2: '2 — Begränsad',
  3: '3 — Bra',
  4: '4 — Utmärkt',
}

const QUARTERS = ['Q1', 'Q2', 'H1', 'Q3', 'Q4', 'H2']

const EMPTY_DATA = {
  date: '',
  summary: '',
  team: [],
  perspectives: {
    personal_development: emptyPerspective(),
    together:             emptyPerspective(),
    value_creation:       emptyPerspective(),
    freedom:              emptyPerspective(),
  },
  recruitment_candidates: [],
  why_this_client: { kpi_aspects: '', outside_kpi_aspects: '' },
}

function parsePeriod(str) {
  const m = str && str.match(/^(Q[1-4]|H[12])\s+(\d{4})$/)
  return m ? { q: m[1], year: parseInt(m[2], 10) } : { q: '', year: new Date().getFullYear() }
}

const Field = ({ label, optional, children }) => (
  <div className="form-block vertical">
    <label>{label}{optional && <span className="cc-optional">(valfritt)</span>}</label>
    {children}
  </div>
)

const val = (e) => (e.target !== undefined ? e.target.value : e.value)

const ClientReviewEdit = () => {
  const { slug, reviewSlug } = useParams()
  const navigate = useNavigate()
  const isNew = !reviewSlug

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showDeleteReviewDialog } = useClientCaseDialog()

  const [activeTab, setActiveTab]   = useState(TABS[0])
  const [clientName, setClientName] = useState('')
  const [periodQ, setPeriodQ]       = useState('')
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear())
  const [formSlug, setFormSlug]     = useState('')
  const [data, setData]             = useState(EMPTY_DATA)
  const [loading, setLoading]       = useState(!isNew)
  const [saving, setSaving]         = useState(false)
  const [employees, setEmployees]   = useState([])

  const period = periodQ ? `${periodQ} ${periodYear}` : ''

  useEffect(() => {
    userService.getAll()
      .then(res => setEmployees((res.users || []).filter(u => !u.user_is_disabled).map(u => u.user_fullname).sort()))
      .catch(() => {})
  }, [])

  // :slug is the CLIENT's own slug (reviews are client-level since PR4) — no more
  // guessing the display name by stripping a "-YYYY-MM" case-slug suffix; fetch
  // the real company name instead.
  useEffect(() => {
    if (slug) {
      clientService.getBySlug(slug)
        .then(res => setClientName(res.client.company_name))
        .catch(() => {})
    }
  }, [slug])

  useEffect(() => {
    if (!isNew) {
      clientReviewService.getBySlug(reviewSlug)
        .then(res => {
          const r = res.clientReview
          const parsed = parsePeriod(r.client_review_period)
          setPeriodQ(parsed.q)
          setPeriodYear(parsed.year)
          setFormSlug(r.client_review_slug)
          const d = r.client_review_data || {}
          setData({
            ...EMPTY_DATA,
            ...d,
            team: d.team || [],
            perspectives: {
              personal_development: { ...emptyPerspective(), ...(d.perspectives?.personal_development || {}) },
              together:             { ...emptyPerspective(), ...(d.perspectives?.together             || {}) },
              value_creation:       { ...emptyPerspective(), ...(d.perspectives?.value_creation       || {}) },
              freedom:              { ...emptyPerspective(), ...(d.perspectives?.freedom              || {}) },
            },
            why_this_client: { kpi_aspects: '', outside_kpi_aspects: '', ...(d.why_this_client || {}) },
          })
          setLoading(false)
        })
        .catch(() => { showMessage('Kunde inte ladda', 'error'); setLoading(false) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, reviewSlug, isNew])

  useEffect(() => {
    if (!isNew) return
    if (clientName && period) {
      setFormSlug(`${slugify(clientName)}-${slugify(period)}`)
    }
  }, [clientName, period, isNew])

  const setPersp = (key, field, value) => {
    setData(prev => ({
      ...prev,
      perspectives: {
        ...prev.perspectives,
        [key]: { ...prev.perspectives[key], [field]: value },
      },
    }))
  }

  const getIndividual = (perspKey, name) =>
    (data.perspectives[perspKey]?.individual || []).find(e => e.name === name) || { name, score: null, comment: '' }

  const setIndividual = (perspKey, name, field, value) => {
    setData(prev => {
      const existing = [...(prev.perspectives[perspKey]?.individual || [])]
      const idx = existing.findIndex(e => e.name === name)
      const entry = idx >= 0
        ? { ...existing[idx], [field]: value }
        : { name, score: null, comment: '', [field]: value }
      if (idx >= 0) existing[idx] = entry; else existing.push(entry)
      return {
        ...prev,
        perspectives: {
          ...prev.perspectives,
          [perspKey]: { ...prev.perspectives[perspKey], individual: existing },
        },
      }
    })
  }

  const setWhy = (field, value) => {
    setData(prev => ({ ...prev, why_this_client: { ...prev.why_this_client, [field]: value } }))
  }

  const setTop = (field, value) => setData(prev => ({ ...prev, [field]: value }))

  const setCandidate = (i, field, value) => {
    setData(prev => {
      const arr = [...(prev.recruitment_candidates || [])]
      arr[i] = { ...arr[i], [field]: value }
      return { ...prev, recruitment_candidates: arr }
    })
  }

  const addCandidate = () => {
    setData(prev => ({
      ...prev,
      recruitment_candidates: [
        ...(prev.recruitment_candidates || []),
        { person_uuid: '', person_name: '', role: '', next_step: '', responsible: '' },
      ],
    }))
  }

  const removeCandidate = (i) => {
    setData(prev => ({ ...prev, recruitment_candidates: prev.recruitment_candidates.filter((_, idx) => idx !== i) }))
  }

  const handleSave = async () => {
    if (!clientName || !period) {
      showMessage('Kundnamn och period krävs', 'error')
      return
    }
    setSaving(true)
    try {
      if (isNew) {
        await clientReviewService.create({ slug: formSlug, client: slug, period, data })
        showMessage('Skapad!')
        navigate(`/${appName}/${slug}/reviews/${formSlug}/edit`)
      } else {
        await clientReviewService.update(reviewSlug, { period, data })
        showMessage('Sparad!')
      }
    } catch (e) {
      showMessage(e.message || 'Kunde inte spara', 'error')
    }
    setSaving(false)
  }

  const deleteReview = () => {
    showDeleteReviewDialog({ clientName, period }, async () => {
      await clientReviewService.remove(reviewSlug)
        .then(() => { hideModalDialog(); showMessage('Borttagen'); navigate(`/${appName}/${slug}/reviews`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  if (loading) return <div className="cc-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  const renderOverview = () => (
    <div className="cc-section">
      <div className="cc-section-title">Översikt</div>

      <div className="cc-row">
        <Field label="Kund">
          {isNew ? (
            <div className="cc-slug-display">{clientName}</div>
          ) : (
            <Input type="text" name="client_name" value={clientName} readOnly />
          )}
        </Field>
        <Field label="Period">
          <div className="cc-period-picker">
            <div className="cc-period-quarters">
              {QUARTERS.map(q => (
                <button
                  key={q}
                  type="button"
                  className={`cc-period-q${periodQ === q ? ' active' : ''}`}
                  onClick={() => setPeriodQ(q)}
                >{q}</button>
              ))}
            </div>
            <div className="cc-period-year">
              <button type="button" className="cc-period-arrow" onClick={() => setPeriodYear(y => y - 1)}>‹</button>
              <span className="cc-period-year-val">{periodYear}</span>
              <button type="button" className="cc-period-arrow" onClick={() => setPeriodYear(y => y + 1)}>›</button>
            </div>
          </div>
        </Field>
      </div>

      <div className="cc-row">
        <Field label="Utvärderingsdatum" optional>
          <input
            className="cc-plain-input"
            type="date"
            value={data.date || ''}
            onChange={e => setTop('date', e.target.value)}
            style={{ maxWidth: 200 }}
          />
        </Field>
        <div style={{ flex: 1 }} />
      </div>

      {isNew && (
        <Field label="URL-slug">
          <div className="cc-slug-display">{formSlug || <span className="cc-slug-placeholder">Genereras automatiskt från kund + period</span>}</div>
        </Field>
      )}

      <Field label="Team hos denna kund">
        <div className="cc-tag-select">
          {(data.team || []).map(name => (
            <span key={name} className="cc-tag">
              {name}
              <button
                type="button"
                className="cc-tag-remove"
                onClick={() => setTop('team', (data.team || []).filter(n => n !== name))}
              >×</button>
            </span>
          ))}
          <select
            className="cc-select cc-tag-add"
            value=""
            onChange={e => {
              const name = e.target.value
              if (name && !(data.team || []).includes(name)) {
                setTop('team', [...(data.team || []), name])
              }
            }}
          >
            <option value="">+ Lägg till medlem…</option>
            {employees.filter(n => !(data.team || []).includes(n)).map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </Field>

      <Field label="Sammanfattning" optional>
        <TextareaAutosize name="summary" value={data.summary} onChange={e => setTop('summary', e.target.value)} minRows={5} placeholder="Övergripande sammanfattning av kvartalet…" />
      </Field>
    </div>
  )

  const renderPerspectives = () => (
    <div className="cc-section">
      <div className="cc-section-title">Perspektiv</div>
      {PERSPECTIVES.map(p => (
        <div className="cc-array-item" key={p.key}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">{p.labelSv}</span>
          </div>
          <Field label="Poäng (1–4)" optional>
            <Select
              name={`persp_${p.key}_score`}
              value={data.perspectives[p.key]?.score ?? ''}
              options={formatSelectOptions({ options: [1, 2, 3, 4].map(n => ({ n, label: SCORE_LABELS[n] })), optionValue: 'n', optionLabel: 'label' })}
              onChange={({ option }) => setPersp(p.key, 'score', option ? Number(option.value) : null)}
              isNullable
              placeholder="Inte satt"
            />
          </Field>
          <Field label="Nuläge" optional>
            <TextareaAutosize name={`persp_${p.key}_current_state`} value={data.perspectives[p.key]?.current_state} onChange={e => setPersp(p.key, 'current_state', e.target.value)} minRows={3} placeholder="Nuläge…" />
          </Field>
          <Field label="Mål" optional>
            <TextareaAutosize name={`persp_${p.key}_goal`} value={data.perspectives[p.key]?.goal} onChange={e => setPersp(p.key, 'goal', e.target.value)} minRows={2} placeholder="Mål…" />
          </Field>
          <Field label="Aktiviteter" optional>
            <TextareaAutosize name={`persp_${p.key}_activities`} value={data.perspectives[p.key]?.activities} onChange={e => setPersp(p.key, 'activities', e.target.value)} minRows={3} placeholder="Aktiviteter för att nå målet…" />
          </Field>

          <div className="cc-individual-section">
            <div className="cc-individual-label">Individuella poäng</div>
            {(data.team || []).length === 0 ? (
              <p className="cc-individual-empty">Inga teammedlemmar — lägg till dem under Översikt</p>
            ) : (
              (data.team || []).map(name => {
                const ind = getIndividual(p.key, name)
                return (
                  <div className="cc-individual-row" key={name}>
                    <span className="cc-individual-name">{name}</span>
                    <div className="cc-score-btns">
                      {[1, 2, 3, 4].map(n => (
                        <button
                          key={n}
                          type="button"
                          className={`cc-score-btn${ind.score === n ? ' active' : ''}`}
                          onClick={() => setIndividual(p.key, name, 'score', ind.score === n ? null : n)}
                        >{n}</button>
                      ))}
                    </div>
                    <input
                      className="cc-input cc-individual-comment"
                      type="text"
                      value={ind.comment || ''}
                      onChange={e => setIndividual(p.key, name, 'comment', e.target.value)}
                      placeholder="Kommentar…"
                    />
                  </div>
                )
              })
            )}
          </div>
        </div>
      ))}
    </div>
  )

  const renderRecruitment = () => (
    <div className="cc-section">
      <div className="cc-section-title">Rekryteringskandidater</div>
      {(data.recruitment_candidates || []).map((c, i) => (
        <div className="cc-array-item" key={i}>
          <div className="cc-array-item-header">
            <span className="cc-array-item-label">{c.person_name || c.name || `Kandidat ${i + 1}`}</span>
            <button className="cc-remove-btn" onClick={() => removeCandidate(i)}>Ta bort</button>
          </div>
          <div className="cc-row">
            <Field label="Namn">
              <PersonPicker
                name={`candidate_${i}_person`}
                value={c.person_uuid}
                onChange={({ value, person }) => {
                  setCandidate(i, 'person_uuid', value)
                  setCandidate(i, 'person_name', person ? person.person_name : '')
                  // Stamp when this person was flagged — the API uses it as the
                  // occurred_at of the network person-event (see person-event-log plan).
                  // Set on selection (and re-set if the person is swapped) so it
                  // reflects the current person, not when the empty row appeared.
                  if (value) setCandidate(i, 'added_timestamp', new Date().toISOString())
                }}
                allowCreate
              />
            </Field>
            <Field label="Roll" optional>
              <Input type="text" name={`candidate_${i}_role`} value={c.role} onChange={e => setCandidate(i, 'role', val(e))} placeholder="Data Engineer" />
            </Field>
          </div>
          <div className="cc-row">
            <Field label="Ansvarig" optional>
              <select
                className="cc-select"
                value={c.responsible || ''}
                onChange={e => setCandidate(i, 'responsible', e.target.value)}
              >
                <option value="">—</option>
                {employees.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Nästa steg" optional>
            <Input type="text" name={`candidate_${i}_next_step`} value={c.next_step} onChange={e => setCandidate(i, 'next_step', val(e))} placeholder="Plan för nästa steg…" />
          </Field>
        </div>
      ))}
      <button className="cc-add-btn" onClick={addCandidate}>+ Lägg till kandidat</button>
    </div>
  )

  const renderWhy = () => (
    <div className="cc-section">
      <div className="cc-section-title">Varför denna kund?</div>
      <Field label="KPI-aspekter" optional>
        <TextareaAutosize name="why_kpi_aspects" value={data.why_this_client?.kpi_aspects} onChange={e => setWhy('kpi_aspects', e.target.value)} minRows={5} placeholder="Aspekter kopplade till Leverans, Affärsutveckling, Sälj, Rekrytering…" />
      </Field>
      <Field label="Aspekter utanför KPI" optional>
        <TextareaAutosize name="why_outside_kpi_aspects" value={data.why_this_client?.outside_kpi_aspects} onChange={e => setWhy('outside_kpi_aspects', e.target.value)} minRows={4} placeholder="Övriga överväganden…" />
      </Field>
    </div>
  )

  return (
    <div className="cc-page">
      <div className="cc-page-header">
        <div>
          <div style={{ marginBottom: 8 }}>
            <Button value="← Utvärderingar" className="button" onClick={() => navigate(`/${appName}/${slug}/reviews`)} />
          </div>
          <h1>{isNew ? 'Ny utvärdering' : `${clientName} — ${period}`}</h1>
        </div>
        {!isNew && (
          <Button value="Ta bort" className="button cc-danger-button" onClick={deleteReview} />
        )}
      </div>

      <div className="cc-form-wrap">
        <FormTabs activeTab={activeTab} onTabChange={setActiveTab}>
          <FormTab label={TABS[0]}>{renderOverview()}</FormTab>
          <FormTab label={TABS[1]}>{renderPerspectives()}</FormTab>
          <FormTab label={TABS[2]}>{renderRecruitment()}</FormTab>
          <FormTab label={TABS[3]}>{renderWhy()}</FormTab>
        </FormTabs>

        <div className="cc-form-footer">
          <Button value={saving ? 'Sparar…' : 'Spara'} className="button" onClick={handleSave} disabled={saving} />
        </div>
      </div>
    </div>
  )
}

export default ClientReviewEdit
